import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { CreditCard, ArrowLeft, Info } from 'lucide-react'
import {
  fetchBookings,
  fetchChildren,
  calculatePrice,
  initiatePayment,
  Booking,
  Child,
  PaymentSummary,
  PaymentType,
  BookingPaymentItem,
} from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export function CheckoutPage() {
  const { t, i18n } = useTranslation(['payments', 'common', 'bookings'])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [bookings, setBookings] = useState<Booking[]>([])
  const [children, setChildren] = useState<Child[]>([])
  const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([])
  const [bookingPaymentTypes, setBookingPaymentTypes] = useState<Record<number, PaymentType>>({})
  const [priceSummary, setPriceSummary] = useState<PaymentSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [calculating, setCalculating] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get child name from ID
  const getChildName = (childId: number) => {
    const child = children.find(c => c.id === childId)
    return child ? `${child.first_name} ${child.last_name}` : ''
  }

  // Determine available payment types for a specific booking
  const getAvailableTypesForBooking = useCallback((booking: Booking): PaymentType[] => {
    if (booking.available_payment_types) return booking.available_payment_types
    if (booking.has_advance_payment) return ['repayment']
    return ['advance', 'full']
  }, [])

  // Detect if any selected booking has early bird pricing
  const isEarlyBirdActive = useMemo(() => {
    return priceSummary?.booking_payment_details?.some(d => d.is_early_bird) ?? false
  }, [priceSummary])

  // Check if a specific booking has early bird pricing
  const isBookingEarlyBird = useCallback((bookingId: number): boolean => {
    return priceSummary?.booking_payment_details?.find(d => d.booking_id === bookingId)?.is_early_bird ?? false
  }, [priceSummary])

  // Check if advance payment equals full price (i.e., no repayment needed)
  const isAdvanceSameAsFull = useCallback((bookingId: number): boolean => {
    if (!priceSummary) return false
    const breakdown = priceSummary.breakdowns.find(b => b.booking_ids.includes(bookingId))
    if (!breakdown) return false
    return breakdown.advance_per_child === breakdown.price_per_child
  }, [priceSummary])

  const getBookingTier = (bookingId: number): string | null => {
    const breakdown = priceSummary?.breakdowns.find(b => b.booking_ids.includes(bookingId))
    return breakdown?.tier ?? null
  }

  // Get payment type label, adjusting for early bird per booking
  const getPaymentTypeLabel = (type: PaymentType, bookingId?: number) => {
    const earlyBird = bookingId != null ? isBookingEarlyBird(bookingId) : false
    if (earlyBird) {
      if (type === 'full') return t('types.earlyBirdFull')
      if (type === 'advance') return t('types.regularAdvance')
    }
    return t(`types.${type}`)
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        const [allBookings, allChildren] = await Promise.all([fetchBookings(), fetchChildren()])
        const payableBookings = allBookings.filter((b) => b.is_payment_eligible === true)
        setBookings(payableBookings)
        setChildren(allChildren)

        const bookingId = searchParams.get('booking')
        if (bookingId) {
          const id = parseInt(bookingId, 10)
          const booking = payableBookings.find((b) => b.id === id)
          if (booking) {
            const availableTypes = booking.available_payment_types
              ? booking.available_payment_types
              : booking.has_advance_payment ? ['repayment'] as PaymentType[] : ['advance', 'full'] as PaymentType[]
            setSelectedBookingIds([id])
            setBookingPaymentTypes({ [id]: availableTypes[0] })
          }
        }
      } catch (err) {
        setError(t('errorLoadingBookings'))
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [searchParams, t])

  // Build BookingPaymentItem[] from current selections
  const bookingPaymentItems: BookingPaymentItem[] = useMemo(() => {
    return selectedBookingIds
      .filter(id => bookingPaymentTypes[id])
      .map(id => ({
        booking_id: id,
        payment_type: bookingPaymentTypes[id],
      }))
  }, [selectedBookingIds, bookingPaymentTypes])

  useEffect(() => {
    const calculate = async () => {
      if (bookingPaymentItems.length === 0) { setPriceSummary(null); return }
      setCalculating(true)
      setError(null)
      try {
        const summary = await calculatePrice(bookingPaymentItems)
        setPriceSummary(summary)
        if (summary.has_errors) setError(summary.error_messages.join('; '))
      } catch (err: any) {
        setError(err.response?.data?.error || t('errorCalculatingPrice'))
        setPriceSummary(null)
      } finally {
        setCalculating(false)
      }
    }
    calculate()
  }, [bookingPaymentItems, t])

  // Auto-switch from 'advance' to 'full' when advance equals full price
  useEffect(() => {
    if (!priceSummary) return
    const updates: Record<number, PaymentType> = {}
    for (const [id, type] of Object.entries(bookingPaymentTypes)) {
      const bookingId = Number(id)
      if (type === 'advance' && isAdvanceSameAsFull(bookingId)) {
        updates[bookingId] = 'full'
      }
    }
    if (Object.keys(updates).length > 0) {
      setBookingPaymentTypes(prev => ({ ...prev, ...updates }))
    }
  }, [priceSummary, isAdvanceSameAsFull])

  const handleBookingToggle = (bookingId: number) => {
    setSelectedBookingIds((prev) => {
      if (prev.includes(bookingId)) {
        // Deselect: remove from payment types map too
        setBookingPaymentTypes((types) => {
          const next = { ...types }
          delete next[bookingId]
          return next
        })
        return prev.filter((id) => id !== bookingId)
      } else {
        // Select: auto-set default payment type
        const booking = bookings.find((b) => b.id === bookingId)
        if (booking) {
          const available = getAvailableTypesForBooking(booking)
          setBookingPaymentTypes((types) => ({
            ...types,
            [bookingId]: available[0],
          }))
        }
        return [...prev, bookingId]
      }
    })
  }

  const handlePaymentTypeChange = (bookingId: number, type: PaymentType) => {
    setBookingPaymentTypes((prev) => ({ ...prev, [bookingId]: type }))
  }

  const handleSelectAll = () => {
    if (selectedBookingIds.length === bookings.length) {
      setSelectedBookingIds([])
      setBookingPaymentTypes({})
    } else {
      const allIds = bookings.map((b) => b.id)
      const types: Record<number, PaymentType> = {}
      bookings.forEach((b) => {
        const available = getAvailableTypesForBooking(b)
        types[b.id] = available[0]
      })
      setSelectedBookingIds(allIds)
      setBookingPaymentTypes(types)
    }
  }

  const handleSubmit = async () => {
    if (!priceSummary || priceSummary.has_errors || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const languageCode = i18n.language === 'el' ? 'el-GR' : 'en-US'
      const response = await initiatePayment(bookingPaymentItems, languageCode)
      navigate('/payments/redirect', {
        state: {
          formAction: response.form_action,
          formFields: response.form_fields,
          amount: response.amount,
          description: response.description,
          merchantReference: response.merchant_reference,
        },
      })
    } catch (err: any) {
      setError(err.response?.data?.error || t('errorInitiatingPayment'))
      setSubmitting(false)
    }
  }

  const formatCurrency = (amount: string | undefined) => {
    if (!amount) return '€0.00'
    const parsed = parseFloat(amount)
    if (isNaN(parsed)) return '€0.00'
    return new Intl.NumberFormat(i18n.language === 'el' ? 'el-GR' : 'en-US', {
      style: 'currency', currency: 'EUR',
    }).format(parsed)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('checkout')}</h1>
        </div>
        <Button variant="outline" onClick={() => navigate('/payments')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('navigation.payments', { ns: 'common' })}
        </Button>
      </div>

      {bookings.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <CreditCard className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">{t('noPayableBookings')}</p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              {t('backToHome', { ns: 'common' })}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Booking Selection - wider */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">{t('selectBookings')}</CardTitle>
                  <button onClick={handleSelectAll} className="text-xs font-medium text-primary hover:underline">
                    {selectedBookingIds.length === bookings.length ? t('deselectAll') : t('selectAll')}
                  </button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {bookings.map((booking) => {
                  const isSelected = selectedBookingIds.includes(booking.id)
                  const availableTypes = getAvailableTypesForBooking(booking)
                    .filter(t => !(t === 'advance' && isAdvanceSameAsFull(booking.id)))
                  const currentType = bookingPaymentTypes[booking.id]

                  return (
                    <div
                      key={booking.id}
                      className={`rounded-lg border transition-all ${
                        isSelected
                          ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20'
                          : 'border-input hover:bg-muted/50'
                      }`}
                    >
                      <label className="flex items-start gap-3 p-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleBookingToggle(booking.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary mt-0.5 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {booking.event_name || `Event #${booking.event}`}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground font-mono">{booking.code}</span>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs text-muted-foreground">{getChildName(booking.child)}</span>
                          </div>
                          {booking.has_advance_payment && (
                            <p className="text-xs text-emerald-700 font-medium mt-1">
                              {t('advancePaymentMade', { defaultValue: 'Advance payment made' })}
                            </p>
                          )}
                        </div>
                        <Badge
                          variant={booking.status === 'confirmed' ? 'success' : 'warning'}
                          className="flex-shrink-0 text-xs"
                        >
                          {t(`bookingsBox.statuses.${booking.status}`, { ns: 'bookings' })}
                        </Badge>
                      </label>

                      {/* Per-booking payment type selector */}
                      {isSelected && availableTypes.length > 1 && (
                        <div className="px-3 pb-3 pt-0 ml-7">
                          <div className="flex gap-2">
                            {availableTypes.map((type) => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => handlePaymentTypeChange(booking.id, type)}
                                className={`px-3 py-1.5 text-xs rounded-md border transition-all ${
                                  currentType === type
                                    ? 'border-primary bg-primary/10 text-primary font-semibold'
                                    : 'border-input text-muted-foreground hover:bg-muted/50'
                                }`}
                              >
                                {getPaymentTypeLabel(type, booking.id)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {isSelected && availableTypes.length === 1 && (
                        <div className="px-3 pb-3 pt-0 ml-7">
                          <span className="text-xs text-muted-foreground">
                            {getPaymentTypeLabel(availableTypes[0], booking.id)}
                          </span>
                        </div>
                      )}
                      {isSelected && availableTypes.includes('reserve_spot') && (
                        <div className="px-3 pb-3 pt-0 ml-7">
                          <div className="rounded-md bg-muted/50 border border-input p-3 flex gap-2">
                            <Info className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-muted-foreground">
                              <Trans
                                i18nKey="reserveSpotInfo"
                                ns="payments"
                                components={{
                                  1: (
                                    <a
                                      href={t('reserveSpotInfoUrl')}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="underline font-medium text-primary hover:text-primary/80"
                                    />
                                  ),
                                }}
                              />
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          </div>

          {/* Price Summary - wider */}
          <div className="lg:col-span-2">
            <Card className="sticky top-20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('summary')}</CardTitle>
              </CardHeader>
              <CardContent>
                {calculating ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-xs text-muted-foreground">{t('calculating')}</p>
                  </div>
                ) : priceSummary ? (
                  <div className="space-y-4">
                    {/* Per-booking payment details */}
                    {priceSummary.booking_payment_details && priceSummary.booking_payment_details.length > 0 && (
                      <div className="space-y-2">
                        {priceSummary.booking_payment_details.map((detail) => {
                          const booking = bookings.find((b) => b.id === detail.booking_id)
                          return (
                            <div key={detail.booking_id} className="text-sm py-3 border-b border-border">
                              <div className="flex justify-between items-start">
                                <div className="min-w-0">
                                  <p className="font-medium text-foreground truncate">
                                    {booking ? getChildName(booking.child) : `Booking #${detail.booking_id}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {booking?.event_name || ''}
                                  </p>
                                  {(() => {
                                    const tier = getBookingTier(detail.booking_id)
                                    return (
                                      <>
                                        {tier === '2' && (
                                          <p className="text-xs text-emerald-600">{t('discountTier2')}</p>
                                        )}
                                        {tier === '3+' && (
                                          <p className="text-xs text-emerald-600">{t('discountTier3Plus')}</p>
                                        )}
                                      </>
                                    )
                                  })()}
                                </div>
                                <div className="text-right flex-shrink-0 ml-2">
                                  <p className="font-medium">{formatCurrency(detail.amount)}</p>
                                  <Badge variant="outline" className="text-xs mt-0.5">
                                    {getPaymentTypeLabel(detail.payment_type, detail.booking_id)}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {isEarlyBirdActive && (
                      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 flex gap-2">
                        <Info className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-emerald-800">{t('earlyBirdInfo')}</p>
                      </div>
                    )}

                    {/* Amount due */}
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-foreground">{t('amountDue')}</span>
                      <span className="text-xl font-bold text-primary">{formatCurrency(priceSummary.amount_due)}</span>
                    </div>

                    {error && (
                      <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                        <p className="text-xs text-destructive">{error}</p>
                      </div>
                    )}

                    <Button
                      onClick={handleSubmit}
                      disabled={submitting || priceSummary.has_errors || parseFloat(priceSummary.amount_due) <= 0}
                      className="w-full"
                      size="lg"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      {submitting ? t('processing') : t('proceedToPayment')}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('selectBookingsToSeePrice')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}
